import { useState, useEffect } from "react";
import { getSettings, updateSettings, runDailyChecks, getExpiringDocuments, getUnpaidCotisationsList, sendDocumentReminders, sendPaymentReminders, purgeAllData, purgeAdherentsData, purgeTotalData, changePassword, getStripeSettings, updateStripeKey } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Settings,
  Building,
  Euro,
  FileText,
  Mail,
  Phone,
  Globe,
  MapPin,
  Save,
  RotateCcw,
  Bell,
  Clock,
  AlertTriangle,
  Send,
  RefreshCw,
  CheckCircle,
  Loader2,
  Trash2,
  AlertOctagon,
  Lock,
  Eye,
  EyeOff,
  CreditCard,
} from "lucide-react";

export default function SettingsPage() {
  const [settings, setSettings] = useState({
    nom_association: "Food Truck United",
    email_contact: "contact@foodtruckunited.fr",
    adresse: "",
    telephone: "",
    site_web: "",
    cotisation_defaut: 150.0,
    frais_evenement_collectif: 20.0,
    mention_legale_tva: "TVA non applicable – article 293 B du CGI",
    footer_facture: "Food Truck United - Association Loi 1901",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Automation state
  const [expiringDocs, setExpiringDocs] = useState(null);
  const [unpaidCotisations, setUnpaidCotisations] = useState(null);
  const [runningChecks, setRunningChecks] = useState(false);
  const [sendingDocReminders, setSendingDocReminders] = useState(false);
  const [sendingPayReminders, setSendingPayReminders] = useState(false);
  const [showPurgeDialog, setShowPurgeDialog] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purgeConfirmText, setPurgeConfirmText] = useState("");
  
  // Purge adherents state
  const [showPurgeAdherentsDialog, setShowPurgeAdherentsDialog] = useState(false);
  const [purgingAdherents, setPurgingAdherents] = useState(false);
  const [purgeAdherentsConfirmText, setPurgeAdherentsConfirmText] = useState("");
  
  // Purge total state
  const [showPurgeTotalDialog, setShowPurgeTotalDialog] = useState(false);
  const [purgingTotal, setPurgingTotal] = useState(false);
  const [purgeTotalConfirmText, setPurgeTotalConfirmText] = useState("");
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Stripe settings
  const [stripeSettings, setStripeSettings] = useState(null);
  const [newStripeKey, setNewStripeKey] = useState("");
  const [showStripeKey, setShowStripeKey] = useState(false);
  const [updatingStripe, setUpdatingStripe] = useState(false);

  useEffect(() => {
    loadSettings();
    loadStripeSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await getSettings();
      setSettings(response.data);
    } catch (error) {
      console.error("Error loading settings:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadStripeSettings = async () => {
    try {
      const response = await getStripeSettings();
      setStripeSettings(response.data);
    } catch (error) {
      console.error("Error loading Stripe settings:", error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(settings);
      toast.success("Paramètres enregistrés");
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({
      nom_association: "Food Truck United",
      email_contact: "contact@foodtruckunited.fr",
      adresse: "",
      telephone: "",
      site_web: "",
      cotisation_defaut: 150.0,
      frais_evenement_collectif: 20.0,
      mention_legale_tva: "TVA non applicable – article 293 B du CGI",
      footer_facture: "Food Truck United - Association Loi 1901",
    });
    toast.info("Paramètres réinitialisés (non sauvegardés)");
  };

  // Automation functions
  const loadAutomationData = async () => {
    try {
      const [docsRes, cotisRes] = await Promise.all([
        getExpiringDocuments(30),
        getUnpaidCotisationsList()
      ]);
      setExpiringDocs(docsRes.data);
      setUnpaidCotisations(cotisRes.data);
    } catch (error) {
      console.error("Error loading automation data:", error);
    }
  };

  const handleRunAllChecks = async () => {
    setRunningChecks(true);
    try {
      const response = await runDailyChecks();
      toast.success(`Vérifications terminées: ${response.data.document_reminders.sent} emails documents, ${response.data.payment_reminders.sent} emails paiements`);
      loadAutomationData();
    } catch (error) {
      toast.error("Erreur lors des vérifications");
    } finally {
      setRunningChecks(false);
    }
  };

  const handleSendDocReminders = async () => {
    setSendingDocReminders(true);
    try {
      const response = await sendDocumentReminders();
      toast.success(`${response.data.sent} rappels documents envoyés`);
      loadAutomationData();
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSendingDocReminders(false);
    }
  };

  const handleSendPayReminders = async () => {
    setSendingPayReminders(true);
    try {
      const response = await sendPaymentReminders();
      toast.success(`${response.data.sent} rappels paiements envoyés`);
      loadAutomationData();
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSendingPayReminders(false);
    }
  };

  const handlePurgeAllData = async () => {
    if (purgeConfirmText !== "SUPPRIMER TOUT") {
      toast.error("Veuillez taper 'SUPPRIMER TOUT' pour confirmer");
      return;
    }
    
    setPurging(true);
    try {
      const response = await purgeAllData();
      toast.success(response.data.message);
      setShowPurgeDialog(false);
      setPurgeConfirmText("");
      // Refresh page after purge
      window.location.reload();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setPurging(false);
    }
  };

  const handlePurgeAdherentsData = async () => {
    if (purgeAdherentsConfirmText !== "RAZ ADHERENTS") {
      toast.error("Veuillez taper 'RAZ ADHERENTS' pour confirmer");
      return;
    }
    
    setPurgingAdherents(true);
    try {
      const response = await purgeAdherentsData();
      toast.success(response.data.message);
      setShowPurgeAdherentsDialog(false);
      setPurgeAdherentsConfirmText("");
      // Refresh page after purge
      window.location.reload();
    } catch (error) {
      toast.error("Erreur lors de la remise à zéro");
    } finally {
      setPurgingAdherents(false);
    }
  };

  const handlePurgeTotalData = async () => {
    if (purgeTotalConfirmText !== "EFFACER TOUT") {
      toast.error("Veuillez taper 'EFFACER TOUT' pour confirmer");
      return;
    }
    
    setPurgingTotal(true);
    try {
      const response = await purgeTotalData();
      toast.success(response.data.message);
      setShowPurgeTotalDialog(false);
      setPurgeTotalConfirmText("");
      // Redirect to login after total purge
      localStorage.removeItem("token");
      window.location.href = "/login";
    } catch (error) {
      toast.error("Erreur lors de la purge totale");
    } finally {
      setPurgingTotal(false);
    }
  };

  const handleChangePassword = async () => {
    // Validation
    if (!passwordData.currentPassword) {
      toast.error("Veuillez entrer votre mot de passe actuel");
      return;
    }
    if (!passwordData.newPassword) {
      toast.error("Veuillez entrer un nouveau mot de passe");
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error("Le nouveau mot de passe doit contenir au moins 6 caractères");
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    
    setChangingPassword(true);
    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      toast.success("Mot de passe modifié avec succès");
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors du changement de mot de passe";
      toast.error(message);
    } finally {
      setChangingPassword(false);
    }
  };

  const handleUpdateStripeKey = async () => {
    if (!newStripeKey.trim()) {
      toast.error("Veuillez entrer une clé Stripe");
      return;
    }
    if (!newStripeKey.startsWith("sk_test_") && !newStripeKey.startsWith("sk_live_")) {
      toast.error("La clé doit commencer par 'sk_test_' ou 'sk_live_'");
      return;
    }
    
    setUpdatingStripe(true);
    try {
      const response = await updateStripeKey(newStripeKey);
      toast.success(response.data.message);
      setNewStripeKey("");
      setShowStripeKey(false);
      loadStripeSettings();
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de la mise à jour";
      toast.error(message);
    } finally {
      setUpdatingStripe(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-black text-primary">
            Paramètres
          </h1>
          <p className="text-muted-foreground mt-1">
            Configuration de l'application
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleReset}
            className="border-[#E5E0DC]"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Réinitialiser
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary hover:bg-primary-hover"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Enregistrer
          </Button>
        </div>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="bg-[#F0EBE6]">
          <TabsTrigger value="general" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            <Building className="w-4 h-4 mr-2" />
            Général
          </TabsTrigger>
          <TabsTrigger value="tarifs" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            <Euro className="w-4 h-4 mr-2" />
            Tarifs
          </TabsTrigger>
          <TabsTrigger value="factures" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            <FileText className="w-4 h-4 mr-2" />
            Factures
          </TabsTrigger>
          <TabsTrigger 
            value="automation" 
            className="data-[state=active]:bg-primary data-[state=active]:text-white"
            onClick={loadAutomationData}
          >
            <Bell className="w-4 h-4 mr-2" />
            Rappels
          </TabsTrigger>
          <TabsTrigger value="security" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            <Lock className="w-4 h-4 mr-2" />
            Sécurité
          </TabsTrigger>
          <TabsTrigger value="danger" className="data-[state=active]:bg-status-error data-[state=active]:text-white">
            <AlertOctagon className="w-4 h-4 mr-2" />
            Zone Danger
          </TabsTrigger>
        </TabsList>

        {/* Général */}
        <TabsContent value="general">
          <Card className="bg-white border-[#E5E0DC]">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                <Building className="w-5 h-5 text-primary" />
                Informations de l'association
              </CardTitle>
              <CardDescription>
                Ces informations apparaissent sur les factures et documents officiels
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-secondary font-semibold flex items-center gap-2">
                    <Building className="w-4 h-4" /> Nom de l'association
                  </Label>
                  <Input
                    value={settings.nom_association}
                    onChange={(e) => setSettings({ ...settings, nom_association: e.target.value })}
                    className="mt-1 border-[#E5E0DC]"
                    placeholder="Food Truck United"
                  />
                </div>
                <div>
                  <Label className="text-secondary font-semibold flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email de contact
                  </Label>
                  <Input
                    type="email"
                    value={settings.email_contact}
                    onChange={(e) => setSettings({ ...settings, email_contact: e.target.value })}
                    className="mt-1 border-[#E5E0DC]"
                    placeholder="contact@foodtruckunited.fr"
                  />
                </div>
                <div>
                  <Label className="text-secondary font-semibold flex items-center gap-2">
                    <Phone className="w-4 h-4" /> Téléphone
                  </Label>
                  <Input
                    value={settings.telephone}
                    onChange={(e) => setSettings({ ...settings, telephone: e.target.value })}
                    className="mt-1 border-[#E5E0DC]"
                    placeholder="01 23 45 67 89"
                  />
                </div>
                <div>
                  <Label className="text-secondary font-semibold flex items-center gap-2">
                    <Globe className="w-4 h-4" /> Site web
                  </Label>
                  <Input
                    value={settings.site_web}
                    onChange={(e) => setSettings({ ...settings, site_web: e.target.value })}
                    className="mt-1 border-[#E5E0DC]"
                    placeholder="https://foodtruckunited.fr"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-secondary font-semibold flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> Adresse
                  </Label>
                  <Textarea
                    value={settings.adresse}
                    onChange={(e) => setSettings({ ...settings, adresse: e.target.value })}
                    className="mt-1 border-[#E5E0DC]"
                    placeholder="Adresse complète de l'association"
                    rows={2}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tarifs */}
        <TabsContent value="tarifs">
          <Card className="bg-white border-[#E5E0DC]">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                <Euro className="w-5 h-5 text-primary" />
                Configuration des tarifs
              </CardTitle>
              <CardDescription>
                Définissez les montants par défaut pour les cotisations et frais
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-secondary font-semibold">Cotisation annuelle par défaut (€)</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    value={settings.cotisation_defaut === 0 ? "" : settings.cotisation_defaut}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSettings({ ...settings, cotisation_defaut: val === "" ? 0 : parseFloat(val) || 0 });
                    }}
                    className="mt-1 border-[#E5E0DC]"
                    placeholder="0.00"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Montant appliqué automatiquement lors de la création d'un nouveau food truck
                  </p>
                </div>
                <div>
                  <Label className="text-secondary font-semibold">Frais événement collectif (€)</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    value={settings.frais_evenement_collectif === 0 ? "" : settings.frais_evenement_collectif}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSettings({ ...settings, frais_evenement_collectif: val === "" ? 0 : parseFloat(val) || 0 });
                    }}
                    className="mt-1 border-[#E5E0DC]"
                    placeholder="0.00"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Montant ajouté automatiquement par participant lors d'un événement collectif
                  </p>
                </div>
              </div>

              <div className="p-4 bg-[#F9F7F5] rounded-lg">
                <h3 className="font-semibold text-secondary mb-2">Récapitulatif</h3>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• Cotisation annuelle: <strong>{settings.cotisation_defaut} €</strong></li>
                  <li>• Frais par événement collectif: <strong>{settings.frais_evenement_collectif} €</strong> par participant</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Factures */}
        <TabsContent value="factures">
          <Card className="bg-white border-[#E5E0DC]">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Configuration des factures
              </CardTitle>
              <CardDescription>
                Personnalisez les informations affichées sur vos factures PDF
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="text-secondary font-semibold">Mention légale TVA</Label>
                <Input
                  value={settings.mention_legale_tva}
                  onChange={(e) => setSettings({ ...settings, mention_legale_tva: e.target.value })}
                  className="mt-1 border-[#E5E0DC]"
                  placeholder="TVA non applicable – article 293 B du CGI"
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Cette mention apparaît en bas de chaque facture
                </p>
              </div>

              <div>
                <Label className="text-secondary font-semibold">Pied de page facture</Label>
                <Textarea
                  value={settings.footer_facture}
                  onChange={(e) => setSettings({ ...settings, footer_facture: e.target.value })}
                  className="mt-1 border-[#E5E0DC]"
                  placeholder="Food Truck United - Association Loi 1901"
                  rows={2}
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Informations affichées dans le pied de page des factures
                </p>
              </div>

              <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <h3 className="font-semibold text-primary mb-2">Aperçu facture</h3>
                <div className="text-sm space-y-2 text-secondary">
                  <p className="font-medium">{settings.nom_association}</p>
                  <p className="text-muted-foreground">{settings.email_contact}</p>
                  <hr className="border-[#E5E0DC] my-2" />
                  <p className="text-muted-foreground">{settings.footer_facture}</p>
                  <p className="italic text-muted-foreground">{settings.mention_legale_tva}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Automation / Rappels */}
        <TabsContent value="automation">
          <div className="space-y-6">
            {/* Actions globales */}
            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-primary" />
                  Actions automatiques
                </CardTitle>
                <CardDescription>
                  Les rappels sont envoyés automatiquement chaque jour à 9h. Vous pouvez aussi les déclencher manuellement.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={handleRunAllChecks}
                  disabled={runningChecks}
                  className="bg-primary hover:bg-primary-hover"
                  data-testid="run-all-checks-btn"
                >
                  {runningChecks ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Lancer toutes les vérifications
                </Button>
              </CardContent>
            </Card>

            {/* Documents expirés */}
            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-status-warning" />
                    Documents expirant bientôt
                  </CardTitle>
                  <CardDescription>
                    Food trucks avec des documents expirant dans les 30 prochains jours
                  </CardDescription>
                </div>
                <Button
                  onClick={handleSendDocReminders}
                  disabled={sendingDocReminders || !expiringDocs?.count}
                  variant="outline"
                  className="border-status-warning text-status-warning hover:bg-status-warning/10"
                >
                  {sendingDocReminders ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Envoyer les rappels
                </Button>
              </CardHeader>
              <CardContent>
                {expiringDocs === null ? (
                  <p className="text-muted-foreground text-center py-4">
                    Cliquez sur l'onglet pour charger les données
                  </p>
                ) : expiringDocs.count === 0 ? (
                  <div className="text-center py-6">
                    <CheckCircle className="w-12 h-12 mx-auto text-status-success mb-2" />
                    <p className="text-muted-foreground">Aucun document expirant bientôt</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {expiringDocs.alerts.map((alert, idx) => (
                      <div key={idx} className="p-3 bg-[#F9F7F5] rounded-lg flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-secondary">{alert.nom_food_truck}</p>
                          <p className="text-sm text-muted-foreground">{alert.email}</p>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {alert.expiring_documents.map((doc, i) => (
                              <Badge key={i} variant="outline" className="border-status-warning text-status-warning">
                                {doc.nom} - {doc.date_expiration}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Cotisations impayées */}
            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                    <Clock className="w-5 h-5 text-status-error" />
                    Cotisations impayées
                  </CardTitle>
                  <CardDescription>
                    Food trucks avec des cotisations en attente de paiement
                  </CardDescription>
                </div>
                <Button
                  onClick={handleSendPayReminders}
                  disabled={sendingPayReminders || !unpaidCotisations?.count}
                  variant="outline"
                  className="border-status-error text-status-error hover:bg-status-error/10"
                >
                  {sendingPayReminders ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Envoyer les relances
                </Button>
              </CardHeader>
              <CardContent>
                {unpaidCotisations === null ? (
                  <p className="text-muted-foreground text-center py-4">
                    Cliquez sur l'onglet pour charger les données
                  </p>
                ) : unpaidCotisations.count === 0 ? (
                  <div className="text-center py-6">
                    <CheckCircle className="w-12 h-12 mx-auto text-status-success mb-2" />
                    <p className="text-muted-foreground">Toutes les cotisations sont à jour</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {unpaidCotisations.alerts.map((alert, idx) => (
                      <div key={idx} className="p-3 bg-[#F9F7F5] rounded-lg flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-secondary">{alert.nom_food_truck}</p>
                          <p className="text-sm text-muted-foreground">{alert.email}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-status-error">{alert.cotisation.montant} €</p>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {alert.cotisation.annee}
                            </Badge>
                            {alert.relances > 0 && (
                              <Badge variant="destructive" className="text-xs">
                                {alert.relances} relance(s)
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Sécurité */}
        <TabsContent value="security">
          <Card className="bg-white border-[#E5E0DC]">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                <Lock className="w-5 h-5 text-primary" />
                Changer le mot de passe
              </CardTitle>
              <CardDescription>
                Modifiez votre mot de passe administrateur
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 max-w-md">
              <div>
                <Label className="text-secondary font-semibold">Mot de passe actuel</Label>
                <div className="relative mt-1">
                  <Input
                    type={showCurrentPassword ? "text" : "password"}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    className="border-[#E5E0DC] pr-10"
                    placeholder="••••••••"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-secondary"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label className="text-secondary font-semibold">Nouveau mot de passe</Label>
                <div className="relative mt-1">
                  <Input
                    type={showNewPassword ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    className="border-[#E5E0DC] pr-10"
                    placeholder="••••••••"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-secondary"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Minimum 6 caractères</p>
              </div>
              
              <div>
                <Label className="text-secondary font-semibold">Confirmer le nouveau mot de passe</Label>
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="mt-1 border-[#E5E0DC]"
                  placeholder="••••••••"
                  data-testid="confirm-password-input"
                />
              </div>
              
              <Button
                onClick={handleChangePassword}
                disabled={changingPassword}
                className="bg-primary hover:bg-primary-hover"
                data-testid="change-password-btn"
              >
                {changingPassword ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Lock className="w-4 h-4 mr-2" />
                )}
                Changer le mot de passe
              </Button>
            </CardContent>
          </Card>

          {/* Stripe Configuration */}
          <Card className="bg-white border-[#E5E0DC] mt-6">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" />
                Configuration Stripe
              </CardTitle>
              <CardDescription>
                Configurez votre clé API Stripe pour les paiements
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Status */}
              {stripeSettings && (
                <div className="p-4 bg-[#F9F7F5] rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-secondary">Clé actuelle :</span>
                    <code className="text-sm bg-white px-2 py-1 rounded border">
                      {stripeSettings.masked_key}
                    </code>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-secondary">Mode :</span>
                    <Badge 
                      className={stripeSettings.mode === "test" 
                        ? "bg-yellow-100 text-yellow-800" 
                        : stripeSettings.mode === "production" 
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }
                    >
                      {stripeSettings.mode === "test" ? "Test" : stripeSettings.mode === "production" ? "Production" : "Non configuré"}
                    </Badge>
                  </div>
                  {stripeSettings.is_emergent_key && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Vous utilisez la clé Emergent par défaut. Configurez votre propre clé Stripe pour recevoir les paiements.
                    </p>
                  )}
                </div>
              )}

              {/* Update Key */}
              <div className="space-y-4 max-w-md">
                <div>
                  <Label className="text-secondary font-semibold">Nouvelle clé Stripe</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showStripeKey ? "text" : "password"}
                      value={newStripeKey}
                      onChange={(e) => setNewStripeKey(e.target.value)}
                      className="border-[#E5E0DC] pr-10"
                      placeholder="sk_test_... ou sk_live_..."
                      data-testid="stripe-key-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowStripeKey(!showStripeKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-secondary"
                    >
                      {showStripeKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Obtenez votre clé sur{" "}
                    <a 
                      href="https://dashboard.stripe.com/apikeys" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      dashboard.stripe.com/apikeys
                    </a>
                  </p>
                </div>

                <Button
                  onClick={handleUpdateStripeKey}
                  disabled={updatingStripe || !newStripeKey.trim()}
                  className="bg-primary hover:bg-primary-hover"
                  data-testid="update-stripe-btn"
                >
                  {updatingStripe ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  Mettre à jour la clé Stripe
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Zone Danger */}
        <TabsContent value="danger">
          <Card className="bg-red-50 border-status-error">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-status-error flex items-center gap-2">
                <AlertOctagon className="w-5 h-5" />
                Zone Danger
              </CardTitle>
              <CardDescription className="text-status-error/70">
                Actions irréversibles - à utiliser avec précaution
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* RAZ Adhérents - Cotisations et Dépenses Bureau uniquement */}
              <div className="p-4 bg-white rounded-lg border border-amber-500/30">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-secondary flex items-center gap-2">
                      <Trash2 className="w-4 h-4 text-amber-600" />
                      RAZ Adhérents (Cotisations & Dépenses)
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Supprime uniquement les cotisations et les achats bureau.
                      <br />
                      <strong className="text-status-success">Les événements, messages, food trucks et tout le reste seront préservés.</strong>
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowPurgeAdherentsDialog(true)}
                    className="border-amber-500 text-amber-600 hover:bg-amber-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    RAZ Adhérents
                  </Button>
                </div>
              </div>

              {/* Purge complète */}
              <div className="p-4 bg-white rounded-lg border border-status-error/30">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-secondary flex items-center gap-2">
                      <Trash2 className="w-4 h-4 text-status-error" />
                      Purger les données opérationnelles
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Supprime les événements, paiements, cotisations, messages et fichiers.
                      <br />
                      <strong className="text-status-success">Les food trucks, utilisateurs, menus et plannings HEC seront préservés.</strong>
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    onClick={() => setShowPurgeDialog(true)}
                    className="bg-status-error hover:bg-status-error/90"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Purger les données
                  </Button>
                </div>
              </div>

              {/* Purge TOTALE */}
              <div className="p-4 bg-white rounded-lg border-4 border-red-600">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-red-700 flex items-center gap-2">
                      <Trash2 className="w-5 h-5 text-red-700" />
                      ⚠️ PURGE TOTALE - Recommencer à zéro
                    </h3>
                    <p className="text-sm text-red-600 mt-1">
                      <strong>SUPPRIME ABSOLUMENT TOUT :</strong> Food trucks, utilisateurs, événements, menus, etc.
                      <br />
                      <strong className="text-red-800">Seul votre compte admin sera conservé.</strong>
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    onClick={() => setShowPurgeTotalDialog(true)}
                    className="bg-red-700 hover:bg-red-800 border-2 border-red-900"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    PURGE TOTALE
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Purge Confirmation Dialog */}
      <AlertDialog open={showPurgeDialog} onOpenChange={setShowPurgeDialog}>
        <AlertDialogContent className="border-status-error">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-status-error flex items-center gap-2">
              <AlertOctagon className="w-5 h-5" />
              Confirmer la purge des données opérationnelles
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                <strong>Cette action est IRRÉVERSIBLE.</strong>
              </p>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="font-semibold text-red-700 mb-2">Données SUPPRIMÉES :</p>
                <ul className="list-disc pl-5 space-y-1 text-sm text-red-600">
                  <li>Tous les événements et participations</li>
                  <li>Toutes les cotisations et paiements</li>
                  <li>Tous les achats bureau</li>
                  <li>Tous les messages et conversations</li>
                  <li>Tous les fichiers uploadés</li>
                  <li>Toutes les factures</li>
                  <li>Tous les bilans de prestations</li>
                </ul>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="font-semibold text-green-700 mb-2">Données PRÉSERVÉES :</p>
                <ul className="list-disc pl-5 space-y-1 text-sm text-green-600">
                  <li>Tous les comptes utilisateurs</li>
                  <li>Tous les food trucks</li>
                  <li>Tous les menus et plats</li>
                  <li>Tous les plannings HEC</li>
                  <li>Les modèles de contrats</li>
                </ul>
              </div>
              
              <div className="mt-4">
                <Label className="text-status-error font-semibold">
                  Tapez "SUPPRIMER TOUT" pour confirmer :
                </Label>
                <Input
                  value={purgeConfirmText}
                  onChange={(e) => setPurgeConfirmText(e.target.value)}
                  placeholder="SUPPRIMER TOUT"
                  className="mt-2 border-status-error/50 focus:border-status-error"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPurgeConfirmText("")}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePurgeAllData}
              disabled={purging || purgeConfirmText !== "SUPPRIMER TOUT"}
              className="bg-status-error hover:bg-status-error/90"
            >
              {purging ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer définitivement
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* RAZ Adhérents Confirmation Dialog */}
      <AlertDialog open={showPurgeAdherentsDialog} onOpenChange={setShowPurgeAdherentsDialog}>
        <AlertDialogContent className="border-amber-500">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-amber-600 flex items-center gap-2">
              <AlertOctagon className="w-5 h-5" />
              Remettre à zéro les cotisations et dépenses bureau
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                <strong>Cette action est IRRÉVERSIBLE.</strong>
              </p>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="font-semibold text-amber-700 mb-2">Données SUPPRIMÉES :</p>
                <ul className="list-disc pl-5 space-y-1 text-sm text-amber-600">
                  <li>Toutes les cotisations</li>
                  <li>Tous les achats bureau</li>
                  <li>Toutes les répartitions d'achats</li>
                </ul>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="font-semibold text-green-700 mb-2">Données PRÉSERVÉES :</p>
                <ul className="list-disc pl-5 space-y-1 text-sm text-green-600">
                  <li>Tous les événements</li>
                  <li>Tous les messages</li>
                  <li>Tous les food trucks et utilisateurs</li>
                  <li>Tous les menus et plannings</li>
                  <li>Toutes les factures</li>
                </ul>
              </div>
              
              <div className="mt-4">
                <Label className="text-amber-600 font-semibold">
                  Tapez "RAZ ADHERENTS" pour confirmer :
                </Label>
                <Input
                  value={purgeAdherentsConfirmText}
                  onChange={(e) => setPurgeAdherentsConfirmText(e.target.value)}
                  placeholder="RAZ ADHERENTS"
                  className="mt-2 border-amber-500/50 focus:border-amber-500"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPurgeAdherentsConfirmText("")}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePurgeAdherentsData}
              disabled={purgingAdherents || purgeAdherentsConfirmText !== "RAZ ADHERENTS"}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {purgingAdherents ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Remise à zéro...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remettre à zéro
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PURGE TOTALE Confirmation Dialog */}
      <AlertDialog open={showPurgeTotalDialog} onOpenChange={setShowPurgeTotalDialog}>
        <AlertDialogContent className="border-4 border-red-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700 flex items-center gap-2 text-xl">
              <AlertOctagon className="w-6 h-6" />
              ⚠️ PURGE TOTALE - DANGER EXTRÊME
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <div className="bg-red-100 border-2 border-red-500 rounded-lg p-4">
                <p className="font-bold text-red-800 text-lg mb-3">
                  🚨 CETTE ACTION VA SUPPRIMER TOUTES LES DONNÉES !
                </p>
                <ul className="list-disc pl-5 space-y-1 text-red-700">
                  <li><strong>Tous les food trucks</strong></li>
                  <li><strong>Tous les utilisateurs</strong> (sauf vous)</li>
                  <li><strong>Tous les événements</strong></li>
                  <li><strong>Tous les menus et plats</strong></li>
                  <li><strong>Toutes les cotisations</strong></li>
                  <li><strong>Tous les messages</strong></li>
                  <li><strong>Tous les fichiers</strong></li>
                  <li><strong>ABSOLUMENT TOUT</strong></li>
                </ul>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="font-semibold text-green-700">Seul élément conservé :</p>
                <p className="text-green-600">✓ Votre compte administrateur</p>
              </div>
              
              <div className="mt-4 bg-yellow-50 border border-yellow-400 rounded-lg p-3">
                <Label className="text-red-700 font-bold text-lg">
                  Tapez "EFFACER TOUT" pour confirmer :
                </Label>
                <Input
                  value={purgeTotalConfirmText}
                  onChange={(e) => setPurgeTotalConfirmText(e.target.value)}
                  placeholder="EFFACER TOUT"
                  className="mt-2 border-2 border-red-500 focus:border-red-700 text-lg font-bold"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPurgeTotalConfirmText("")}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePurgeTotalData}
              disabled={purgingTotal || purgeTotalConfirmText !== "EFFACER TOUT"}
              className="bg-red-700 hover:bg-red-800 text-lg font-bold"
            >
              {purgingTotal ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Suppression en cours...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  EFFACER TOUT
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
